<!--
/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software: you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation, either
 * version 3 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with OrangeHRM.
 * If not, see <https://www.gnu.org/licenses/>.
 */
 -->

<template>
  <div class="orangehrm-comment">
    <div class="orangehrm-comment-profile">
      <div class="orangehrm-comment-profile-image-wrapper">
        <img
          alt="profile picture"
          class="orangehrm-comment-profile-image"
          :src="imgSrc"
        />
      </div>
    </div>
    <div class="orangehrm-comment-body">
      <div class="orangehrm-comment-profile-name">
        <oxd-text type="subtitle-2">
          {{ fullName }}
        </oxd-text>
      </div>
      <div class="orangehrm-comment-message">
        <oxd-text type="subtitle-2">
          {{ commentText }}
        </oxd-text>
        <a
          v-if="attachmentUrl"
          class="orangehrm-comment-attachment"
          :href="attachmentUrl"
          target="_blank"
          rel="noopener"
        >
          View attachment{{ attachmentName ? `: ${attachmentName}` : '' }}
        </a>
      </div>
      <div class="orangehrm-comment-timestamp">
        <oxd-text type="subtitle-2">
          {{ commentDate }} - {{ data.time }}
        </oxd-text>
      </div>
    </div>
  </div>
</template>

<script>
import {computed} from 'vue';
import useDateFormat from '@/core/util/composable/useDateFormat';
import {formatDate, parseDate} from '@/core/util/helper/datefns';
import useLocale from '@/core/util/composable/useLocale';
const defaultPic = `${window.appGlobal.publicPath}/images/default-photo.png`;

export default {
  name: 'LeaveComment',
  props: {
    data: {
      type: Object,
      required: true,
    },
  },
  setup(props) {
    const imgSrc = computed(() => {
      const employee = props.data.createdByEmployee;
      return employee
        ? `${window.appGlobal.baseUrl}/pim/viewPhoto/empNumber/${employee.empNumber}`
        : defaultPic;
    });

    const fullName = computed(() => {
      const employee = props.data.createdByEmployee;
      return employee && `${employee.firstName} ${employee.lastName}`;
    });

    const attachment = computed(() => {
      const comment = String(props.data?.comment || '');
      const match = comment.match(/Attachment:\s*(.*?)\s+(\S*\/uploads\/group-hr-leave\/\S+)/i);
      if (!match) return null;
      const path = match[2].replace(/[.,);]+$/, '');
      const relativePath = path.match(/(?:^|\/)uploads\/(group-hr-leave\/[^/]+\/[^/]+)$/i)?.[1];
      if (!relativePath) return null;
      return {
        name: match[1],
        url: `${window.appGlobal.publicPath}/group-hr-pwa-api.php?action=leave-attachment-view&file=${encodeURIComponent(`uploads/${relativePath}`)}`,
      };
    });

    const commentText = computed(() => {
      const comment = String(props.data?.comment || '');
      return attachment.value ? comment.replace(/\s*Attachment:\s*.*?\s+\S*\/uploads\/group-hr-leave\/\S+/i, '').trim() : comment;
    });

    const {jsDateFormat} = useDateFormat();
    const {locale} = useLocale();

    const commentDate = computed(() => {
      return formatDate(parseDate(props.data?.date), jsDateFormat, {locale});
    });

    return {
      imgSrc,
      fullName,
      attachmentName: computed(() => attachment.value?.name || ''),
      attachmentUrl: computed(() => attachment.value?.url || ''),
      commentText,
      commentDate,
    };
  },
};
</script>

<style src="./leave-comment.scss" lang="scss" scoped></style>
